import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { KeyRound, LogIn, ShieldCheck, ScanLine, Mail, Lock } from 'lucide-react';
import { useAuth } from '@/shared/auth/AuthProvider';
import { useTheme } from '@/shared/theme/ThemeProvider';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ApiError } from '@/shared/api/client';
import { useBarcodeScanner } from '@/shared/scanner/GlobalBarcodeListener';

type Mode = 'password' | 'rfid';

export function LoginPage() {
  const { user, loading, loginPassword, loginRfid } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [rfid, setRfid] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => { emailRef.current?.focus(); }, []);

  useBarcodeScanner({
    enabled: mode === 'rfid',
    minLength: 4,
    captureInInputs: true,
    onScan: async (code) => {
      if (!orgSlug) { setError('Informe o slug da organização para login por RFID'); return; }
      setRfid(code);
      try {
        setSubmitting(true);
        setError(null);
        await loginRfid(code, orgSlug);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Falha no login por RFID');
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (loading) {
    return <div className="grid h-full place-items-center text-sm text-neutral-500">Carregando…</div>;
  }
  if (user) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  async function onSubmitPassword(e: FormEvent) {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);
      await loginPassword(email.trim(), password, orgSlug.trim() || undefined);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha no login');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <aside className="hidden flex-col justify-between bg-brand-600 p-10 text-white lg:flex">
        <div className="flex items-center gap-3">
          <ShieldCheck size={28} strokeWidth={2} />
          <span className="text-xl font-semibold">ICTUS CME</span>
        </div>
        <div className="max-w-md space-y-4">
          <h1 className="text-3xl font-semibold leading-tight">Rastreabilidade total do material esterilizado.</h1>
          <p className="text-sm text-brand-100">
            Plataforma multi-tenant aderente à RDC 15/ANVISA. Controla ciclo de vida de instrumentais,
            kits, lotes de esterilização, indicadores biológicos, quarentena, recall em cascata e
            integração com ERPs.
          </p>
        </div>
        <div className="text-xxs text-brand-200">v0.2 · ambiente {import.meta.env.MODE}</div>
      </aside>

      <section className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-brand-600 dark:text-brand-300">
              <ShieldCheck size={20} />
              <span className="text-md font-semibold text-neutral-800 dark:text-neutral-100">Entrar</span>
            </div>
            <button
              type="button"
              onClick={toggle}
              className="ict-focus-ring rounded px-2 py-1 text-xs text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-100"
            >
              {theme === 'dark' ? 'Tema claro' : 'Tema escuro'}
            </button>
          </div>

          <div className="flex rounded border border-neutral-300 p-0.5 text-xs dark:border-neutral-700">
            <button
              type="button"
              className={`flex-1 rounded px-3 py-1.5 ${mode === 'password' ? 'bg-brand-500 text-white' : 'text-neutral-600 dark:text-neutral-300'}`}
              onClick={() => { setMode('password'); setError(null); }}
            >
              Email e senha
            </button>
            <button
              type="button"
              className={`flex-1 rounded px-3 py-1.5 ${mode === 'rfid' ? 'bg-brand-500 text-white' : 'text-neutral-600 dark:text-neutral-300'}`}
              onClick={() => { setMode('rfid'); setError(null); }}
            >
              Crachá RFID
            </button>
          </div>

          {mode === 'password' ? (
            <form onSubmit={onSubmitPassword} className="space-y-3">
              <Input
                ref={emailRef}
                label="Email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                leadingIcon={<Mail size={16} />}
              />
              <Input
                label="Senha"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                leadingIcon={<Lock size={16} />}
              />
              <Input
                label="Organização (opcional)"
                type="text"
                placeholder="ex.: demo"
                value={orgSlug}
                onChange={(e) => setOrgSlug(e.target.value)}
                hint="Apenas se o mesmo email existir em mais de uma organização."
                leadingIcon={<KeyRound size={16} />}
              />
              {error ? <p className="text-xs text-danger">{error}</p> : null}
              <Button type="submit" size="lg" block loading={submitting} leadingIcon={<LogIn size={18} />}>
                Entrar
              </Button>
            </form>
          ) : (
            <div className="space-y-3">
              <Input
                label="Organização (slug)"
                placeholder="ex.: demo"
                value={orgSlug}
                onChange={(e) => setOrgSlug(e.target.value)}
                leadingIcon={<KeyRound size={16} />}
              />
              <div className="rounded border border-dashed border-brand-300 bg-brand-50 p-4 text-center dark:border-brand-700 dark:bg-brand-900/30">
                <ScanLine size={32} className="mx-auto mb-2 text-brand-600 dark:text-brand-300" />
                <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100">Aproxime o crachá do leitor</p>
                <p className="mt-1 text-xs text-neutral-500">O scanner HID será capturado automaticamente.</p>
                {rfid ? <p className="mt-2 text-xxs font-mono text-neutral-500">Último: {rfid}</p> : null}
              </div>
              {error ? <p className="text-xs text-danger">{error}</p> : null}
              {submitting ? <p className="text-xs text-neutral-500">Validando…</p> : null}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
